import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018-2021 semantics GmbH
// Date        : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf, Andreas Hesse
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 163319 $
// Purpose     : dialogs
// =============================================================================
// ---Third-party imports
import React, { useState } from 'react';
import { Header, Segment } from 'semantic-ui-react';
import { Rnd } from 'uix/chunks';
import KeyHandler, { KEYDOWN } from 'react-key-handler';
// ---Framework imports
import { isNumber } from 'ldash';
import { isSSR } from 'uix/utils';
import { usePersistentShallow, useSelector } from 'uix/hooks';
import { Sel } from 'uix/registries';
import log from 'uix/logging';
import { XMenu, HeaderSegment, AutoPortal, SplitPane } from 'uix/components';
// ---Local imports
import { ContextHelp } from './parts.js';
// ---Exports
const INITIAL_OPTIONS = {
    size: {
        width: 640,
        height: 400
    },
    position: {
        x: null,
        y: null
    }
};
export const HelpDialog = ({ name = 'unnamedDlg', icon, children, header, onClose, wikiident, buttons, closeOnEsc = false, minWidth = 480, minHeight = 300 })=>{
    let [opts, setOpts] = usePersistentShallow(name, INITIAL_OPTIONS, 2);
    let [help, setHelp] = useState(false);
    let [firstOpened, setFirstOpened] = useState(true);
    let i18n = useSelector(Sel.i18n);
    let size = {
        width: opts.size.width,
        height: opts.size.height
    };
    // log.x(window.innerWidth, window.innerHeight)
    let { x, y } = opts.position;
    if (!isSSR) {
        if (!isNumber(x)) {
            // centered
            x = window.innerWidth / 2 - size.width / 2;
        }
        if (!isNumber(y)) {
            // 30% from top
            y = window.innerHeight / 3 - size.height / 2;
        }
        if (firstOpened) {
            if (x + size.width > window.innerWidth) {
                x = Math.max(0, window.innerWidth - size.width);
            }
            if (y + size.height + 8 > window.innerHeight) {
                y = Math.max(0, window.innerHeight - size.height - 8);
            }
        }
    } else {
        x = x || 0;
        y = y || 0;
    }
    let position = {
        x,
        y
    };
    let style = {
        border: 'solid 2px #ddd',
        background: '#f0f0f0',
        zIndex: 50
    };
    let style2 = {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        paddingLeft: '2px',
        paddingRight: '2px',
        height: '100%'
    };
    let styleHeader = {
        cursor: 'move',
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        padding: '0.1em 0.1em 0.1em 0.6em'
    };
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        padded: true,
        floated: "right",
        children: [
            wikiident && /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "help",
                active: help,
                onClick: ()=>setHelp(!help),
                title: i18n.get('help')
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "close",
                onClick: ()=>onClose(),
                title: i18n.get('Close')
            })
        ]
    });
    let handleClass = `dialog_handle_${name}`;
    return /*#__PURE__*/ _jsx(AutoPortal, {
        target: "rootPortal",
        children: /*#__PURE__*/ _jsxs(Rnd, {
            minWidth: minWidth,
            minHeight: minHeight,
            style: style,
            bounds: "window",
            size: size,
            position: position,
            onDragStop: (e, d)=>{
                setOpts({
                    position: {
                        x: d.x,
                        y: d.y
                    }
                });
                setFirstOpened(false);
            },
            onResize: (e, direction, ref, delta, position)=>{
                setOpts({
                    size: {
                        width: ref.offsetWidth,
                        height: ref.offsetHeight
                    },
                    position
                });
                setFirstOpened(false);
            },
            dragHandleClassName: handleClass,
            children: [
                closeOnEsc && /*#__PURE__*/ _jsx(KeyHandler, {
                    keyEventName: KEYDOWN,
                    code: 'Escape',
                    onKeyHandle: onClose
                }),
                /*#__PURE__*/ _jsxs(SplitPane, {
                    vertical: true,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            style: style2,
                            children: [
                                /*#__PURE__*/ _jsx(HeaderSegment, {
                                    attached: "top",
                                    className: handleClass,
                                    style: styleHeader,
                                    icon: icon,
                                    header: header,
                                    menu: menu
                                }),
                                /*#__PURE__*/ _jsx(Segment, {
                                    attached: true,
                                    style: {
                                        flex: '1 1 auto',
                                        padding: '0em'
                                    },
                                    children: children
                                }),
                                /*#__PURE__*/ _jsx(Header, {
                                    attached: "bottom",
                                    style: {
                                        flex: '0 0 auto',
                                        border: 'none'
                                    },
                                    children: buttons
                                })
                            ]
                        }),
                        help && /*#__PURE__*/ _jsx(ContextHelp, {
                            beta: false,
                            wikiident: wikiident
                        })
                    ]
                })
            ]
        })
    });
};
